/**
 * PMT trust feature — <pmt-trust-media>, the click-to-play facade for
 * sections/pmt-trust-feature.liquid.
 *
 * Until a visitor presses play the media half is a poster, a scrim and a button.
 * The player markup sits in a <template>, which the browser parses into an inert
 * fragment, so no video source, no embed and no third-party request exists on the
 * page. This element clones that fragment on click. It never builds HTML from
 * strings, so the section needs no script-src or unsafe-inline allowance.
 *
 * Everything is scoped to `this`: the section can legitimately appear twice on a
 * page, and a custom element is also what survives the Section Rendering API and
 * the theme editor's re-render, where a document.querySelector pass would not.
 *
 * The button is hidden by CSS until connectedCallback sets data-enhanced, so a
 * visitor whose JS never arrives sees a clean still rather than a dead control.
 */

const PMT_TF_LOADING_TIMEOUT = 1200;

class PmtTrustMedia extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced) return;

    this.playButton = this.querySelector('[data-pmt-tf-play]');
    this.playerTemplate = this.querySelector('[data-pmt-tf-player]');
    if (!this.playButton || !this.playerTemplate) return;

    this.onActivate = this.onActivate.bind(this);
    this.warm = this.warm.bind(this);

    this.playButton.addEventListener('click', this.onActivate);
    this.playButton.addEventListener('pointerenter', this.warm);
    this.playButton.addEventListener('focus', this.warm);

    this.dataset.enhanced = 'true';
  }

  disconnectedCallback() {
    clearTimeout(this.loadingTimer);
  }

  /**
   * First hover or focus is the earliest honest signal of intent, so the embed's
   * host is only preconnected then — never on page load.
   */
  warm() {
    const host = this.playerTemplate.dataset.pmtTfPreconnect;
    this.playButton.removeEventListener('pointerenter', this.warm);
    this.playButton.removeEventListener('focus', this.warm);
    if (!host) return;
    if (document.head.querySelector('link[rel="preconnect"][href="' + host + '"]')) return;

    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = host;
    document.head.appendChild(link);
  }

  onActivate() {
    const fragment = this.playerTemplate.content.cloneNode(true);
    const player = fragment.querySelector('video, iframe');
    if (!player) return;

    const isVideo = player.tagName === 'VIDEO';

    if (!isVideo) {
      // Autoplay is added here and only here: the src in the template has no
      // autoplay parameter, so nothing can start before the visitor asks.
      try {
        const url = new URL(player.src);
        url.searchParams.set('autoplay', '1');
        player.src = url.toString();
      } catch (error) {
        // A malformed embed URL is the merchant's to fix; play it un-autoplayed
        // rather than failing the click.
      }
    }

    this.classList.add('is-loading');
    this.playButton.remove();
    this.appendChild(fragment);
    this.player = player;

    if (isVideo) {
      player.addEventListener('playing', () => this.ready(), { once: true });
      // Capture: a failing <source> fires 'error' at the source, and it does not bubble.
      player.addEventListener('error', () => this.fail(), true);

      const started = player.play();
      if (started && typeof started.catch === 'function') {
        // A blocked play is not a failure — the controls are right there.
        started.catch(() => this.ready());
      }
    } else {
      player.addEventListener('load', () => this.ready(), { once: true });
      // An embed gives no reliable "playing" event, so the bar is time-boxed.
      // It is never left spinning.
      this.loadingTimer = setTimeout(() => this.ready(), PMT_TF_LOADING_TIMEOUT);
    }

    // Keyboard users land on the player they just opened.
    player.focus({ preventScroll: true });
  }

  ready() {
    clearTimeout(this.loadingTimer);
    this.classList.remove('is-loading');
    this.classList.add('is-playing');
  }

  /**
   * Put the facade back rather than leaving a black box or a spinner. The button
   * keeps its listeners across removal, so a second press works.
   */
  fail() {
    clearTimeout(this.loadingTimer);
    this.classList.remove('is-loading', 'is-playing');
    if (this.player) {
      this.player.remove();
      this.player = null;
    }
    this.appendChild(this.playButton);
    this.playButton.focus({ preventScroll: true });
  }
}

if (!customElements.get('pmt-trust-media')) {
  customElements.define('pmt-trust-media', PmtTrustMedia);
}
